import { Injectable } from '@angular/core';
import { DatabaseService } from '../alcance/services/database.service';
import { DatabaseRelation } from '../alcance/interfaces/database';
import * as moment from 'moment';

@Injectable({
    providedIn: 'root'
})
export class PlantaoDBService extends DatabaseService {
    override classname: string = 'PlantaoDBService';
    override table = 'plantao';
    override keyPath = 'uuid';
    override typeKey = 'TEXT';
    protected override useUuidKey = true;
    protected override migrationChunkSize = 50;
    private readonly maxLocalValueLength = 5 * 1024 * 1024;

    protected override migration: number = 2;

    override indices = [
        { name: "by_id", field: "id", unique: false, multiEntry: false },
        { name: "by_edificacao", field: "edificacao_id", unique: false, multiEntry: false },
        { name: "by_data", field: "dt_plantao", unique: false, multiEntry: false },
        { name: "by_sinc", field: "sinc", unique: false, multiEntry: false }
    ];

    protected override relations: DatabaseRelation[] = [
        { name: 'usuario', table: 'usuario', field: 'usuario_id', type: 'foreign' }
        // {name: 'usuarios', table: '', field: '', type: 'pivot'},
    ];

    protected override collectObj(obj: any) {
        obj.dt_plantao = (moment as any)(obj.dt_plantao).format('YYYY-MM-DD HH:mm:ss');
        return super.collectObj(obj);
    }

    private hasFormularioMetadata(metadata: any): boolean {
        return Array.isArray(metadata?.campos) && metadata.campos.length > 0;
    }

    private async assertLocalValueReadable(key: number | string, index: string | null = null): Promise<void> {
        const field = index || this.keyPath;
        const result = await DatabaseService.DB.executeSql('SELECT length(value) as size FROM ' + this.table + ' WHERE ' + field + ' = ?', [key]);

        if(result.rows.length === 0){
            return;
        }

        const size = Number(result.rows.item(0).size || 0);

        if(size > this.maxLocalValueLength){
            const error: any = new Error('Este registro possui dados offline antigos muito grandes. Conecte-se à internet e abra novamente para atualizar os dados.');
            error.code = 'LOCAL_RECORD_TOO_LARGE';
            throw error;
        }
    }

    private async assertLocalListReadable(): Promise<void> {
        const result = await DatabaseService.DB.executeSql('SELECT count(*) as total FROM ' + this.table + ' WHERE length(value) > ?', [this.maxLocalValueLength]);

        if(Number(result.rows.item(0).total || 0) > 0){
            const error: any = new Error('Existem registros offline antigos muito grandes. Conecte-se à internet para atualizar os dados antes de abrir esta lista.');
            error.code = 'LOCAL_RECORD_TOO_LARGE';
            throw error;
        }
    }

    private isLocalValueTooLarge(object: any): boolean {
        try{
            return JSON.stringify(object).length > this.maxLocalValueLength;
        }catch(_){
            return true;
        }
    }

    private compactRelationArray(items: any[] | undefined, compactItem: (item: any) => any): any[] | undefined {
        if(!Array.isArray(items)){
            return items;
        }

        return items.map((item) => compactItem({...item}));
    }

    private compactForLocalCache(object: any): any {
        const compact = {...object};

        compact.formularios = this.compactRelationArray(compact.formularios, (item) => {
            item.metadata = {campos: []};
            delete item.imagem;
            delete item.imagens;
            delete item.file;
            return item;
        });

        compact.ocorrencias = this.compactRelationArray(compact.ocorrencias, (item) => {
            item.execucao_rotinas = {atividades: [], pendencias: []};
            delete item.imagem;
            delete item.imagens;
            delete item.file;
            return item;
        });

        compact.atendimentos = this.compactRelationArray(compact.atendimentos, (item) => {
            delete item.imagem;
            delete item.imagens;
            delete item.file;
            return item;
        });

        compact.liberacoes = this.compactRelationArray(compact.liberacoes, (item) => {
            delete item.imagem;
            delete item.imagens;
            delete item.file;
            return item;
        });

        compact.atividades = this.compactRelationArray(compact.atividades, (item) => {
            delete item.imagem;
            delete item.imagens;
            delete item.file;
            return item;
        });

        return compact;
    }

    override async sinc(object: any): Promise<any> {
        return super.sinc(this.isLocalValueTooLarge(object) ? this.compactForLocalCache(object) : object);
    }

    override async getByKey(key: number | string, index: string | null = null): Promise<any> {
        if(!this.initialized){
            await this.createTable();
            return this.getByKey(key, index);
        }

        await this.assertLocalValueReadable(key, index);
        return super.getByKey(key, index);
    }

    override async paginate(filter: any = {}): Promise<any> {
        await this.assertLocalListReadable();
        return super.paginate(filter);
    }

    override async getAll(params: any = null, headers: any = null): Promise<any> {
        await this.assertLocalListReadable();
        return super.getAll(params, headers);
    }

    private mergeCachedArray(next: any, cached: any, field: string): any {
        if(!Array.isArray(cached?.[field]) || cached[field].length === 0){
            return next;
        }

        if(!Array.isArray(next[field]) || next[field].length === 0){
            next[field] = cached[field];
            return next;
        }

        const cachedById = new Map(cached[field]
            .filter((item: any) => item?.id)
            .map((item: any) => [item.id, item]));

        next[field] = next[field].map((item: any) => {
            const cachedItem = item?.id ? cachedById.get(item.id) : null;

            if(!cachedItem){
                return item;
            }

            const merged = {...(cachedItem as any), ...item};

            if(field === 'formularios' && !this.hasFormularioMetadata(merged.metadata) && this.hasFormularioMetadata((cachedItem as any).metadata)){
                merged.metadata = (cachedItem as any).metadata;
            }

            return merged;
        });

        return next;
    }

    override async sincArray(array: any[]): Promise<any> {
        if(!array || array.length === 0){
            return super.sincArray(array);
        }

        const data: any[] = [];

        for(const item of array){
            const next = {...item};

            if(next[this.keyPath]){
                try{
                    const cached = await this.getByKey(next[this.keyPath]);
                    this.mergeCachedArray(next, cached, 'formularios');
                    this.mergeCachedArray(next, cached, 'ocorrencias');
                    this.mergeCachedArray(next, cached, 'atendimentos');
                    this.mergeCachedArray(next, cached, 'liberacoes');
                    this.mergeCachedArray(next, cached, 'assinaturas');
                    this.mergeCachedArray(next, cached, 'atividades');
                }catch(_){
                    // Plantao ainda nao foi aberto/cacheado com detalhes completos.
                }
            }

            data.push(this.isLocalValueTooLarge(next) ? this.compactForLocalCache(next) : next);
        }

        return super.sincArray(data);
    }

    protected override filterSql(sql: string, params: any): string {
        if(params?.dt_plantao){
            if(sql.indexOf('WHERE') == -1)
                sql += ' WHERE';
            else
                sql += ' AND';

            sql += " by_data >= '" + (moment as any)(params.dt_plantao).startOf('day').format('YYYY-MM-DD HH:mm:ss') + "'";
            sql += " AND by_data <= '" + (moment as any)(params.dt_plantao).endOf('day').format('YYYY-MM-DD HH:mm:ss') + "'";
        }

        if(params?.dt_i){
            if(sql.indexOf('WHERE') == -1)
                sql += ' WHERE';
            else
                sql += ' AND';

            sql += " by_data >= '" + (moment as any)(params.dt_i).startOf('day').format('YYYY-MM-DD HH:mm:ss') + "'";
        }

        if(params?.dt_f){
            if(sql.indexOf('WHERE') == -1)
                sql += ' WHERE';
            else
                sql += ' AND';

            sql += " by_data <= '" + (moment as any)(params.dt_f).endOf('day').format('YYYY-MM-DD HH:mm:ss') + "'";
        }

        return sql;
    }
}
